import { ApiClient } from '@/api/client';
import { WalletJsonld } from '@/api/generated';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TRANSACTIONS_PER_PAGE } from 'constants/itemsPerPage';
import useApi from 'hooks/useApi';
import useUserApi from 'hooks/UserApi/useUserApi';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { clearContextHash, useCmsData } from 'utils/cms';
import marketPlaceFactory from 'utils/factories/marketplace';

const PAYOUT_WAITING_TIME = 5000;

export const useMyWallet = () => {
  const apiClient = useApi();
  const { user } = useUserApi();
  const {
    generalSettings: { marketplace },
  } = useCmsData();
  const queryResult = useQuery({
    queryKey: marketPlaceFactory.wallet(),
    queryFn: async () => apiClient.payments.apiPaymentswalletsbalanceGet(),
    select: ({ data }) => data,
    enabled: marketplace && !!user,
  });

  const isWalletActivated =
    queryResult.data?.shippingAddress &&
    queryResult.data?.billingAddress &&
    queryResult.data?.twoFactorEnabled &&
    queryResult.data?.walletCreated;

  return { queryResult, isWalletActivated };
};

export const usePostPayout = ({ onSuccess }: { onSuccess?: () => void }) => {
  const apiClient = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['postPayout'],
    mutationFn: async () => apiClient.payments.apiPaymentspayoutPost({}),
    onSuccess: () => {
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: marketPlaceFactory.wallet() });
        queryClient.invalidateQueries({ queryKey: marketPlaceFactory.walletHistory() });
        onSuccess?.();
      }, PAYOUT_WAITING_TIME);
    },
  });
};

export const useActivateWallet = () => {
  const apiClient = useApi();
  const { i18n } = useTranslation();

  return useMutation({
    mutationKey: ['activateWallet'],
    mutationFn: async (data: WalletJsonld) => apiClient.payments.apiPaymentswalletsactivatePost(data),
    onSuccess: () => {
      clearContextHash();
      window.location.href = `${i18n.language}/manage-account/my-wallet/my-balance`;
    },
  });
};

type WalletHistoryQueryParams = Parameters<ApiClient['payments']['apiPaymentswalletstransactionsGetCollection']>[0];

export const useWalletHistoryApi = () => {
  const apiClient = useApi();
  const { user } = useUserApi();

  const {
    query: { page, order },
  } = useRouter();

  const queryParams: WalletHistoryQueryParams = {
    itemsPerPage: TRANSACTIONS_PER_PAGE,
    'order[date]': order === 'asc' ? 'asc' : 'desc',
    ...(typeof page === 'string' && { page: parseInt(page, 10) }),
  };

  const queryResult = useQuery({
    queryKey: marketPlaceFactory.walletHistory(queryParams),
    queryFn: async () => apiClient.payments.apiPaymentswalletstransactionsGetCollection(queryParams),
    select: (res) => res.data,
    enabled: !!user,
  });

  return { queryResult };
};

export const useWalletHistoryPayinDetailApi = ({ id }: { id: string }) => {
  const apiClient = useApi();
  const { user } = useUserApi();

  const queryResult = useQuery({
    queryKey: marketPlaceFactory.walletHistoryDetail({
      id,
      type: 'payin',
    }),
    queryFn: async () => apiClient.payments.apiPaymentsdetailpayinIdGet(id),
    enabled: !!user,
  });

  return { queryResult };
};
export const useWalletHistoryPayoutDetailApi = ({ id }: { id: string }) => {
  const apiClient = useApi();
  const { user } = useUserApi();

  const queryResult = useQuery({
    queryKey: marketPlaceFactory.walletHistoryDetail({
      id,
      type: 'payout',
    }),
    queryFn: async () => apiClient.payments.apiPaymentsdetailpayoutIdGet(id),
    enabled: !!user,
  });

  return { queryResult };
};

export const useWalletPayoutInfo = () => {
  const apiClient = useApi();
  const { user } = useUserApi();

  const queryResult = useQuery({
    queryKey: marketPlaceFactory.payout(),
    queryFn: async () => apiClient.payments.apiPaymentspayoutGet(),
    enabled: !!user,
  });

  return { queryResult };
};

export const useWalletHistoryPaymentDetailApi = ({ id }: { id: string }) => {
  const apiClient = useApi();
  const { user } = useUserApi();

  const queryResult = useQuery({
    queryKey: marketPlaceFactory.walletHistoryDetail({
      id,
      type: 'buy',
    }),
    queryFn: async () => apiClient.payments.apiPaymentsdetailIdGet(id),
    enabled: !!user,
  });

  return { queryResult };
};

export const useGetInvoicePdf = ({ id }: { id: string }) => {
  const apiClient = useApi();

  const queryResult = useQuery({
    queryKey: marketPlaceFactory.invoices(id),
    queryFn: async () => apiClient.invoices.apiInvoicesIdpdfGet(id),
    // manualy fired via refetch
    enabled: false,
  });

  return { queryResult };
};

export const useGetSalesSummary = () => {
  const apiClient = useApi();
  const { user } = useUserApi();

  const queryResult = useQuery({
    queryKey: marketPlaceFactory.salesSummary(),
    queryFn: async () => apiClient.marketplace.apiMarketplacesalesSummaryGet(),
    enabled: !!user,
  });

  return { queryResult };
};
